import { useState } from 'react';
import { Plus, Trash2, ChevronRight } from 'lucide-react';
import type { Criterion } from '../types';

interface HierarchyBuilderProps {
  goal: string;
  onGoalChange: (goal: string) => void;
  criteria: Criterion[];
  onCriteriaChange: (criteria: Criterion[]) => void;
  onNext: () => void;
}

export function HierarchyBuilder({
  goal,
  onGoalChange,
  criteria,
  onCriteriaChange,
  onNext,
}: HierarchyBuilderProps) {
  const [newCriterionName, setNewCriterionName] = useState('');

  const topLevelCriteria = criteria.filter(c => c.parentId === null);

  const addCriterion = () => {
    if (!newCriterionName.trim()) return;

    const newCriterion: Criterion = {
      id: `criterion-${Date.now()}`,
      name: newCriterionName.trim(),
      parentId: null,
      localWeight: 0,
      globalWeight: 0,
      level: 1,
    };

    onCriteriaChange([...criteria, newCriterion]);
    setNewCriterionName('');
  };

  const removeCriterion = (id: string) => {
    // Remove criterion and all its sub-criteria
    const removeRecursive = (criterionId: string): string[] => {
      const toRemove = [criterionId];
      criteria.forEach(c => {
        if (c.parentId === criterionId) {
          toRemove.push(...removeRecursive(c.id));
        }
      });
      return toRemove;
    };

    const idsToRemove = removeRecursive(id);
    onCriteriaChange(criteria.filter(c => !idsToRemove.includes(c.id)));
  };

  const canProceed = goal.trim().length > 0 && topLevelCriteria.length >= 2;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Step 1: Define Your Goal</h2>
        <p className="text-[var(--text-sec)] mb-4">
          What decision are you trying to make? This will be the top-level goal for your hierarchy.
        </p>
        <input
          type="text"
          value={goal}
          onChange={(e) => onGoalChange(e.target.value)}
          placeholder="e.g., Select High Value Portfolio for 2024"
          className="w-full px-4 py-3 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500 text-lg"
        />
      </div>

      <div>
        <h2 className="text-2xl font-bold text-[var(--text-pri)] mb-2">Step 2: Define Strategic Criteria</h2>
        <p className="text-[var(--text-sec)] mb-4">
          Add the criteria you'll use to evaluate options. Aim for 5-9 criteria to avoid cognitive overload.
        </p>

        {/* Add Criterion Input */}
        <div className="flex gap-2 mb-6">
          <input
            type="text"
            value={newCriterionName}
            onChange={(e) => setNewCriterionName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addCriterion();
              }
            }}
            placeholder="e.g., Financial Value, Strategic Alignment, Risk"
            className="flex-1 px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-consulting-slate-blue-500 focus:border-consulting-slate-blue-500"
          />
          <button
            onClick={addCriterion}
            className="px-4 py-2 bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700 flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </div>

        {/* Criteria List */}
        {topLevelCriteria.length === 0 ? (
          <div className="text-center py-8 text-[var(--text-ter)] border-2 border-dashed border-[var(--rule)]">
            <p>No criteria added yet. Add at least 2 criteria to continue.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {topLevelCriteria.map((criterion) => (
              <div
                key={criterion.id}
                className="flex items-center justify-between p-4 bg-[var(--bg-raised)] border border-[var(--rule)] hover:border-consulting-slate-blue-300 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <ChevronRight className="h-5 w-5 text-[var(--text-ter)]" />
                  <span className="font-medium text-[var(--text-pri)]">{criterion.name}</span>
                </div>
                <button
                  onClick={() => removeCriterion(criterion.id)}
                  className="p-2 text-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {topLevelCriteria.length > 0 && topLevelCriteria.length < 2 && (
          <div className="mt-4 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)] text-sm">
            ⚠️ You need at least 2 criteria to perform pairwise comparisons.
          </div>
        )}

        {topLevelCriteria.length > 9 && (
          <div className="mt-4 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-[var(--mustard)] text-sm">
            ⚠️ You have more than 9 criteria. Consider grouping related criteria to avoid cognitive overload (Miller's Law).
          </div>
        )}
      </div>

      {/* Next Button */}
      <div className="flex justify-end pt-6 border-t border-[var(--rule)]">
        <button
          onClick={onNext}
          disabled={!canProceed}
          className={`px-6 py-3  font-semibold transition-colors ${
            canProceed
              ? 'bg-consulting-slate-blue-600 text-white hover:bg-consulting-slate-blue-700'
              : 'bg-[var(--rule-bright)] text-[var(--text-ter)] cursor-not-allowed'
          }`}
        >
          Next: Weight Criteria
        </button>
      </div>
    </div>
  );
}

